import { useEffect, useState } from "react";
import { AuthContext } from "@/context/AuthContext";
import { useAuthState } from "@/hooks/useAuthState";
import { useAuthActions } from "@/hooks/useAuthActions";
import { supabase } from "@/lib/supabase";
import { fetchUserProfile } from "@/utils/auth";
import { ErrorHandler, ErrorCategory, ErrorSeverity } from "@/utils/errorHandler";
import { getBrowserInfo } from "@/utils/mobileDetection";

/**
 * AuthProvider manages the authentication state and user profile data.
 * It ensures that user data is fully loaded before marking the state as ready,
 * preventing premature access checks and redirects.
 */
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    user,
    session,
    userRole,
    isAdmin,
    organizationId,
    subscriptionStatus,
    hasValidSubscription,
    subscriptionLoading,
    setUser,
    setSession,
    setUserRole,
    setIsAdmin,
    setOrganizationId,
    setSubscriptionStatus,
    setHasValidSubscription,
    setSubscriptionLoading,
  } = useAuthState();

  // Single ready state to track full initialization
  const [ready, setReady] = useState(false);
  const [showDebugButton, setShowDebugButton] = useState(false);
  const [showFullDebug, setShowFullDebug] = useState(false);
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);
  const [browserInfo] = useState(() => getBrowserInfo());
  
  // Error handler
  const errorHandler = new ErrorHandler("AuthProvider");

  // Combined loading flag
  const loading = !ready;

  const { signIn, signUp, signOut: supaSignOut } = useAuthActions({
    setLoading: () => {}, // No longer using this specific loading setter
  });

  // Show debug button after 5 seconds of loading, and browser warning for problematic browsers
  useEffect(() => {
    if (!ready) {
      const timer = setTimeout(() => {
        setShowDebugButton(true);
        // Show browser warning if we have authentication issues and user is a parent
        if (browserInfo.hasAuthIssues && user?.user_metadata?.role === 'parent') {
          setShowBrowserWarning(true);
        }
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShowDebugButton(false);
      setShowBrowserWarning(false);
    }
  }, [ready, browserInfo.hasAuthIssues, user?.user_metadata?.role]);

  const signOut = async () => {
    const result = await supaSignOut();
    if (!result.error) {
      setUser(null);
      setSession(null);
      setUserRole(null);
      setIsAdmin(false);
      setOrganizationId(null);
      setSubscriptionStatus(null);
      setHasValidSubscription(false);
      setSubscriptionLoading(false);
    }
    return result;
  };

  // First effect: initialize and set up Supabase auth state change listener
  useEffect(() => {
    // Flag to track active component
    let isActive = true;
    
    const initializeAuth = async () => {
      try {
        // Set up auth listener first to catch any auth events during initialization
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
          if (!isActive) return;
          
          setSession(newSession);
          setUser(newSession?.user ?? null);
          
          // On sign out, ensure we reset state
          if (event === 'SIGNED_OUT') {
            setUserRole(null);
            setIsAdmin(false);
            setOrganizationId(null);
            setSubscriptionStatus(null);
            setHasValidSubscription(false);
            setSubscriptionLoading(false);
            setReady(true); // Ready immediately when signed out
          }
          // For other auth events, we'll fetch profile in the second effect
        });
        
        // Get initial session
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (isActive) {
          setSession(initialSession);
          setUser(initialSession?.user ?? null);
          
          // If no session, mark as ready immediately
          if (!initialSession) {
            setReady(true);
          }
          // If there is a session, the second effect will fetch the profile and set ready
        }
        
        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        errorHandler.handleError(
          error,
          "Failed to initialize auth",
          { category: ErrorCategory.AUTHENTICATION },
          true,
          ErrorSeverity.ERROR
        );
        
        // On error, make sure we don't block the UI
        if (isActive) {
          setReady(true);
        }
      }
    };
    
    initializeAuth();
    
    // Cleanup
    return () => {
      isActive = false;
    };
  }, []);

  // Second effect: fetch profile data when user changes
  useEffect(() => {
    if (!user) {
      // No user means no profile to fetch - we should be ready
      setReady(true);
      return;
    }
    
    let isActive = true;
    
    // Immediately set state from metadata if available
    if (user.user_metadata) {
      const metadata = user.user_metadata;
      const metadataRole = metadata.role || 'user';
      const metadataOrgId = metadata.organization_id || null;
      const metadataIsAdmin = ['bizadmin', 'owner', 'superadmin'].includes(metadataRole);
      
      // Set initial state from metadata
      setUserRole(metadataRole);
      setIsAdmin(metadataIsAdmin);
      setOrganizationId(metadataOrgId);
      // Start loading subscription status
      setSubscriptionLoading(true);
      
      // Mark as ready immediately since we have the essential data
      setReady(true);
    }
    
    // Attempt to fetch user profile data in the background to update/validate metadata
    const fetchProfile = async () => {
      try {
        const { 
          role, 
          isAdmin: adminFlag, 
          organizationId: orgId, 
          subscriptionStatus: subStatus,
          hasValidSubscription: validSub
        } = await fetchUserProfile(
          user.id, 
          user.email || '', 
          user
        );
        
        if (!isActive) return;
        
        // Update state with fresh database data
        setUserRole(role);
        setIsAdmin(adminFlag);
        setOrganizationId(orgId);
        setSubscriptionStatus(subStatus);
        setHasValidSubscription(validSub);
        setSubscriptionLoading(false);
        
        // Already marked as ready above, no need to set again
      } catch (error) {
        if (!isActive) return;
        
        // Debug the ErrorHandler issue
        console.log('ErrorHandler instance:', errorHandler);
        console.log('handleError method:', errorHandler.handleError);
        console.log('typeof handleError:', typeof errorHandler.handleError);
        
        // Guard the call to prevent "m is not a function" error
        if (typeof errorHandler.handleError === 'function') {
          errorHandler.handleError(
            error,
            "Error fetching profile",
            {
              userId: user?.id,
              category: ErrorCategory.DATABASE
            },
            true,
            ErrorSeverity.ERROR
          );
        } else {
          console.error('handleError is not a function:', errorHandler.handleError);
          // Fallback - just log the error
          console.error('Profile fetch error:', error);
        }
        
        // Stop subscription loading on error
        setSubscriptionLoading(false);
        
        // If we didn't have metadata, ensure we're still ready
        if (!user.user_metadata?.role) {
          setUserRole('user');
          setIsAdmin(false);
          setOrganizationId(null);
          setSubscriptionStatus(null);
          setHasValidSubscription(false);
          setReady(true);
        }
        // If we had metadata, we're already ready and the metadata values are still valid
      }
    };
    
    // Fetch profile in background (don't reset ready state)
    fetchProfile();
    
    // Cleanup
    return () => {
      isActive = false;
    };
  }, [user?.id]); // Only depend on user.id, not the entire user object




  // Add refreshUser method
  const refreshUser = async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (!error && data?.user) {
        setUser(data.user);
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };


  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userRole,
        isAdmin,
        signIn,
        signUp,
        signOut,
        loading,
        isLoading: loading, // Alias for loading
        ready, // Expose the ready state
        organizationId,
        subscriptionStatus,
        hasValidSubscription,
        subscriptionLoading,
        refreshUser,
      }}
    >
      {ready ? children : (
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="text-center max-w-md w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 mb-6">Setting up your account...</p>
            
            {/* Browser compatibility warning */}
            {showBrowserWarning && browserInfo.hasAuthIssues && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                <div className="flex items-start space-x-3">
                  <div className="text-yellow-600 text-xl">⚠️</div>
                  <div>
                    <h3 className="font-medium text-yellow-800 mb-2">Browser Compatibility Issue</h3>
                    <p className="text-sm text-yellow-700 mb-3">
                      You're using {browserInfo.browserName} on iOS, which has known authentication issues. 
                      For the best experience, please try opening this page in <strong>Safari</strong>.
                    </p>
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          // Try to open in Safari using iOS deep linking
                          const currentUrl = window.location.href;
                          // This will prompt the user to open in Safari
                          window.open(`x-web-search://?${currentUrl}`, '_blank');
                        }}
                        className="block w-full bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm font-medium"
                      >
                        📱 Open in Safari
                      </button>
                      <p className="text-xs text-yellow-600">
                        Or manually copy the URL and paste it into Safari
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {showDebugButton && (
              <div className="space-y-4">
                <button
                  onClick={() => setShowFullDebug(!showFullDebug)}
                  className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  🐛 Show Debug Info
                </button>
                
                {user?.user_metadata?.role === 'parent' && (
                  <button
                    onClick={() => window.location.href = '/parent-portal'}
                    className="block w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg font-medium"
                  >
                    🚀 Force Navigate to Parent Portal
                  </button>
                )}
                
                <button
                  onClick={() => window.location.reload()}
                  className="block w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm"
                >
                  🔄 Reload Page
                </button>
              </div>
            )}
            
            {showFullDebug && (
              <div className="mt-6 p-4 bg-gray-100 rounded-lg text-left text-xs">
                <h3 className="font-bold mb-2">Debug Information</h3>
                <div className="space-y-1 font-mono">
                  <div>User: {user ? 'YES' : 'NO'}</div>
                  <div>User Role: {userRole || 'undefined'}</div>
                  <div>Metadata Role: {user?.user_metadata?.role || 'undefined'}</div>
                  <div>Loading: {loading ? 'YES' : 'NO'}</div>
                  <div>Ready: {ready ? 'YES' : 'NO'}</div>
                  <div>Is Admin: {isAdmin ? 'YES' : 'NO'}</div>
                  <div>Organization ID: {organizationId || 'undefined'}</div>
                  <div>Browser: {browserInfo.browserName}</div>
                  <div>Mobile: {browserInfo.isMobile ? 'YES' : 'NO'}</div>
                  <div>iOS: {browserInfo.isIOS ? 'YES' : 'NO'}</div>
                  <div>Has Auth Issues: {browserInfo.hasAuthIssues ? 'YES' : 'NO'}</div>
                  {user?.user_metadata && (
                    <div className="mt-2">
                      <div className="font-bold">User Metadata:</div>
                      <pre className="text-xs bg-white p-2 rounded mt-1 overflow-auto">
                        {JSON.stringify(user.user_metadata, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
